import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';
import { useIntegrations } from '../../contexts/IntegrationsContext';
import { getPermittedNavigation } from '../../utils/simplePermissions';
import {
  MagnifyingGlassIcon,
  DocumentTextIcon,
  CalendarDaysIcon,
  CurrencyDollarIcon,
  Cog6ToothIcon,
  PlusIcon,
  CreditCardIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  ClockIcon,
  FolderIcon,
  BellIcon,
  PaintBrushIcon,
  ShieldCheckIcon,
  ArrowPathIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  TagIcon
} from '@heroicons/react/24/outline';

// Comprehensive command palette actions - organized by category
// Everything that is NOT a page: quick actions, settings tabs, billing, integrations.
// Page destinations are derived from the nav builder inside the component — see the
// NAVIGATION note below.
const staticActions = [
  // === QUICK ACTIONS (Create New) ===
  { id: 'new-quote', label: 'Create New Quote', keywords: 'new quote create add estimate', path: '/quotes?new=quote', category: 'Quick Actions', icon: PlusIcon },
  { id: 'new-customer', label: 'Create New Customer', keywords: 'new customer create add client', path: '/customers?new=customer', category: 'Quick Actions', icon: PlusIcon },
  // REMOVED: 'new-job' - jobs are created from quotes, not directly
  // REMOVED: 'new-invoice' - invoices are created ONLY from completed jobs
  //   (InvoicesService.createFromWorkOrder); there is no manual/standalone invoice creation, and the
  //   old /invoices?new=invoice link was dead (Invoices.js doesn't read ?new=). Matches the "+ New" menu.
  { id: 'new-employee', label: 'Add New Employee', keywords: 'new employee create add team member staff', path: '/employees?new=employee', category: 'Quick Actions', icon: PlusIcon },

  // === NAVIGATION (Go To) ===
  // Intentionally EMPTY. These 12 entries used to be hardcoded here, and the sidebar
  // built its own list from getPermittedNavigation — two sources for one fact. They
  // drifted: 18 of the nav's 31 destinations had no palette entry, including
  // /team-chat, /expenses, /vendors, /purchase-orders and /inventory. A user could
  // see a page in the sidebar and fail to find it in search.
  //
  // Navigation actions are now DERIVED from the same builder the sidebar uses, inside
  // the component (it needs user + integrations). Adding a page to the nav puts it in
  // the palette automatically, and a page the user has no access to stays out of both.

  // === SETTINGS (uses ?tab= query param pattern) ===
  { id: 'settings-company', label: 'Company Settings', keywords: 'settings company business info profile', path: '/settings?tab=company', category: 'Settings', icon: BuildingOfficeIcon },
  { id: 'settings-branding', label: 'Appearance & Theme', keywords: 'settings branding logo colors theme appearance dark light mode', path: '/settings?tab=appearance', category: 'Settings', icon: PaintBrushIcon },
  { id: 'settings-services', label: 'Service Tags', keywords: 'settings services tags categories what we do', path: '/settings?tab=service-tags', category: 'Settings', icon: TagIcon },
  { id: 'settings-rates', label: 'Rates & Pricing', keywords: 'settings rates pricing hourly rate employee pay', path: '/settings?tab=rates', category: 'Settings', icon: CurrencyDollarIcon },
  { id: 'settings-rate-cards', label: 'Rate Cards', keywords: 'settings rate cards pricing catalog services', path: '/settings?tab=rate-cards', category: 'Settings', icon: CurrencyDollarIcon },
  { id: 'settings-notifications', label: 'Notification Settings', keywords: 'settings notifications alerts emails sms', path: '/settings?tab=notifications', category: 'Settings', icon: BellIcon },
  { id: 'settings-security', label: 'Security Settings', keywords: 'settings security permissions roles access', path: '/settings?tab=security', category: 'Settings', icon: ShieldCheckIcon },
  { id: 'settings-scheduling', label: 'Smart Scheduling', keywords: 'settings scheduling calendar auto assign', path: '/settings?tab=scheduling', category: 'Settings', icon: CalendarDaysIcon },
  { id: 'settings-invoicing', label: 'Invoicing Settings', keywords: 'settings invoicing billing payment terms document templates template invoice footer pdf net 30 custom terms', path: '/settings?tab=invoicing', category: 'Settings', icon: CurrencyDollarIcon },
  // 'Document Templates' removed 2026-08-31 with the tab itself (see
  // docs/PRODUCT_DECISIONS.md). The keywords are folded into Invoicing, which is where
  // the invoice footer and terms that tab appeared to control are actually configured —
  // leaving the old entry would have sent anyone searching "templates" to the empty
  // "Select a setting" state.
  // Same name as the Settings sidebar ('Org Structure'); searchable by the page's own words (#4480 #4500).
  { id: 'settings-approvals', label: 'Org Structure', keywords: 'settings approvals org structure tree hierarchy reporting managers manager threshold', path: '/settings?tab=approvals', category: 'Settings', icon: UserGroupIcon },
  { id: 'settings-quote-acceptance', label: 'Quote Acceptance Settings', keywords: 'settings quote acceptance signature deposit scheduling', path: '/settings?tab=quote-acceptance', category: 'Settings', icon: DocumentTextIcon },
  { id: 'settings-business', label: 'Business Settings', keywords: 'settings business operational preferences policies timezone time zone currency business hours', path: '/settings?tab=business', category: 'Settings', icon: BuildingOfficeIcon },
  { id: 'settings-timezone', label: 'Time Zone', keywords: 'timezone time zone tz clock pacific mountain central eastern arizona alaska hawaii pt mt ct et utc daylight saving local time', path: '/settings?tab=business', category: 'Settings', icon: ClockIcon },
  { id: 'settings-marketplace', label: 'Marketplace Settings', keywords: 'settings marketplace emergency service opt in nights weekend', path: '/settings?tab=marketplace', category: 'Settings', icon: BuildingOfficeIcon },
  { id: 'settings-general', label: 'Settings Overview', keywords: 'settings general preferences overview all', path: '/settings', category: 'Settings', icon: Cog6ToothIcon },

  // === INTEGRATIONS (all go to integrations tab) ===
  { id: 'integration-stripe', label: 'Stripe Payment Settings', keywords: 'stripe payments credit card debit connect integration api payment processing', path: '/settings?tab=integrations', category: 'Integrations', icon: CreditCardIcon },
  { id: 'integration-quickbooks', label: 'QuickBooks Integration', keywords: 'quickbooks qb accounting sync integration api', path: '/settings?tab=integrations', category: 'Integrations', icon: ArrowPathIcon },
  { id: 'integration-google', label: 'Google Calendar Sync', keywords: 'google calendar sync integration api gcal', path: '/settings?tab=integrations', category: 'Integrations', icon: CalendarDaysIcon },
  // #4692 #4695: this pointed at Integrations, which has no email settings. Email is a channel
  // switch on the Notifications tab, so that is where it goes.
  { id: 'integration-email', label: 'Email Notifications', keywords: 'email settings notifications emails', path: '/settings?tab=notifications', category: 'Settings', icon: EnvelopeIcon },
  // SMS is a prepaid add-on bought on Billing, not a credential-based integration.
  //
  // Keywords are deliberately over-broad here. Texting lives on the BILLING tab,
  // which nobody guesses — an operator could only find it by already knowing
  // where it was. Someone looking for this types "text", "texting", "phone",
  // "phone number", "twilio", "reply" or "opt in", and every one of those has to
  // land. "phone" and "number" were both missing before and returned nothing.
  // The Billing page ITSELF had no entry — only the two SMS sub-features below, which both happen
  // to live on it. So searching "billing" returned three unrelated results, "subscription" returned
  // nothing at all, and "stripe" returned only the Stripe Payment Settings page, which is a
  // different thing. A contractor could not reach the page that manages their paid plan by
  // searching for it, including while standing on it.
  //
  // Listed first in this category so the page outranks its own sub-features: someone typing
  // "billing" wants the page, and someone who wants to buy texts types "sms" or "credits".
  // Keywords cover the words a user would actually reach for — plan, upgrade, invoice, card,
  // cancel — not just the page's own title.
  { id: 'settings-billing', label: 'Billing & Subscription', keywords: 'billing subscription plan upgrade downgrade payment method credit card invoice receipt renew cancel trial pricing tier account', path: '/settings?tab=billing', category: 'Billing', icon: CreditCardIcon },
  { id: 'sms-credits', label: 'SMS Credits (buy texts)', keywords: 'sms text texting twilio messaging credits buy balance top up phone number message customers', path: '/settings?tab=billing', category: 'Billing', icon: PhoneIcon },
  { id: 'sms-number', label: 'Texting Number (two-way SMS)', keywords: 'sms text texting twilio phone number my own number two way 2 way reply replies inbound toll free verification a2p 10dlc compliance opt in consent shared number dedicated', path: '/settings?tab=billing', category: 'Billing', icon: PhoneIcon },
  { id: 'integration-maps', label: 'Maps & Routing', keywords: 'maps google routing directions gps integration api', path: '/settings?tab=integrations', category: 'Integrations', icon: MapPinIcon },
  { id: 'settings-integrations', label: 'All Integrations', keywords: 'integrations api connections apps third party', path: '/settings?tab=integrations', category: 'Integrations', icon: ArrowPathIcon },

  // === SEARCH (dynamic) ===
  { id: 'search-customers', label: 'Search Customers...', keywords: 'find search customers', path: '/customers?q=', category: 'Search', icon: MagnifyingGlassIcon, isSearch: true },
  { id: 'search-quotes', label: 'Search Quotes...', keywords: 'find search quotes', path: '/quotes?q=', category: 'Search', icon: MagnifyingGlassIcon, isSearch: true },
  { id: 'search-jobs', label: 'Search Jobs...', keywords: 'find search jobs', path: '/jobs?q=', category: 'Search', icon: MagnifyingGlassIcon, isSearch: true },
  { id: 'search-invoices', label: 'Search Invoices...', keywords: 'find search invoices', path: '/invoices?q=', category: 'Search', icon: MagnifyingGlassIcon, isSearch: true },
];

// Category order for display.
//
// This list used to be the ONLY source of renderable categories, which made it a
// silent kill switch: `groupedResults` iterates it, so any action whose category
// was missing here got filtered IN by the search and then dropped at grouping —
// no error, no result, no way to tell from the action definition that it could
// never appear. 'Billing' was missing, so the SMS credits and texting-number
// entries were unreachable for every query, including the ones whose keywords
// were written specifically to find them.
//
// Unknown categories are now appended instead of discarded, so adding an action
// with a new category can never again make it invisible.
const CATEGORY_ORDER = ['Quick Actions', 'Navigation', 'Settings', 'Billing', 'Integrations', 'Search'];

const CommandPalette = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { user } = useUser();
  const { integrations } = useIntegrations();
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Page destinations come from the SAME builder the sidebar uses, so the palette
  // cannot drift from the menu again and respects the user's permissions — a page
  // they cannot open no longer appears in search.
  const allActions = useMemo(() => {
    const nav = [];
    const walk = (entries, groupName) => {
      for (const e of entries || []) {
        if (e.type === 'group' && Array.isArray(e.items)) { walk(e.items, e.name); continue; }
        // '#' is a group placeholder in the nav, not a destination.
        if (!e.href || e.href === '#') continue;
        nav.push({
          id: `goto-${e.href.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '')}`,
          label: e.name,
          // The group name is searchable too, so "team" finds Team Chat and
          // "operations" finds what lives under Operations.
          keywords: `${e.name} ${groupName || ''} ${e.href.replace(/[^a-z]+/gi, ' ')}`.toLowerCase(),
          path: e.href,
          category: 'Navigation',
          // Always an icon: the renderer does `const Icon = action.icon` and would
          // throw on undefined.
          icon: e.icon || MagnifyingGlassIcon
        });
      }
    };
    try {
      walk(getPermittedNavigation(user, integrations || {}));
    } catch {
      // Never let a nav-builder failure take the whole palette down — the static
      // actions below still work.
    }
    const seen = new Set();
    const uniqueNav = nav.filter((a) => (seen.has(a.path) ? false : seen.add(a.path)));
    return [...staticActions, ...uniqueNav];
  }, [user, integrations]);

  // Filter actions based on query - search label AND keywords
  const filtered = query.trim() === ''
    ? allActions.slice(0, 8) // Show top 8 when no query
    : allActions.filter(a => {
        const searchText = `${a.label} ${a.keywords} ${a.category}`.toLowerCase();
        const terms = query.trim().toLowerCase().split(' ');
        return terms.every(term => searchText.includes(term));
      });

  // Known categories first, then anything else in the order it appears — a new
  // category is displayed last rather than silently swallowed.
  const categoryOrder = [
    ...CATEGORY_ORDER,
    ...[...new Set(allActions.map(a => a.category))].filter(c => !CATEGORY_ORDER.includes(c))
  ];

  // Group filtered results by category
  const groupedResults = categoryOrder.reduce((acc, category) => {
    const items = filtered.filter(a => a.category === category);
    if (items.length > 0) {
      acc.push({ category, items });
    }
    return acc;
  }, []);

  // Flatten for keyboard navigation
  const flatResults = groupedResults.flatMap(g => g.items);

  const handleKeyDown = useCallback((e) => {
    if (!isOpen) return;

    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, flatResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (flatResults[selectedIndex]) {
        run(flatResults[selectedIndex]);
      }
    }
  }, [isOpen, flatResults, selectedIndex, onClose]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  useEffect(() => {
    if (isOpen) {
      // Reset on OPEN, not on close. `run()` clears the query, but Escape and the
      // overlay call onClose() directly, and `if (!isOpen) return null` only stops
      // rendering — the component stays mounted and its state survives. So the next
      // open replayed the previous search.
      setQuery('');
      setSelectedIndex(0);
      if (inputRef.current) inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current && flatResults[selectedIndex]) {
      const selectedEl = listRef.current.querySelector(`[data-index="${selectedIndex}"]`);
      if (selectedEl) {
        selectedEl.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex, flatResults]);

  const run = (action) => {
    onClose();
    setQuery('');
    if (action.isSearch && action.path.endsWith('?q=')) {
      // For search actions, append the query
      navigate(action.path + encodeURIComponent(query));
    } else {
      navigate(action.path);
    }
  };

  if (!isOpen) return null;

  let globalIndex = -1;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[10vh]">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />

      {/* Modal — role/aria-modal match GlassModal.js:87, which is the pattern the
          rest of the app already uses (Expenses.js:1094, PurchaseOrders.js:292).
          CmdK simply never inherited it: a grep for `role=` in this file returned
          zero hits. It is the app's primary keyboard navigation surface, so it is
          the worst one to announce nothing.

          The heading is visually hidden rather than omitted: aria-modal needs an
          accessible name, and there is no visible title to point aria-labelledby
          at without adding chrome nobody asked for. */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="command-palette-title"
        className="command-palette relative rounded-xl shadow-2xl border w-full max-w-xl mx-4 overflow-hidden"
      >
        <h2 id="command-palette-title" className="sr-only">Command palette</h2>
        {/* Search Input */}
        <div className="command-palette__search flex items-center px-4 border-b border-gray-200 dark:border-slate-700">
          <MagnifyingGlassIcon className="w-5 h-5 text-gray-400" aria-hidden="true" />
          <input
            ref={inputRef}
            autoFocus
            type="text"
            role="combobox"
            aria-label="Search for pages, settings and actions"
            aria-autocomplete="list"
            aria-controls="command-palette-results"
            aria-expanded={flatResults.length > 0}
            aria-activedescendant={
              flatResults[selectedIndex] ? `command-palette-option-${selectedIndex}` : undefined
            }
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search for pages, settings, actions..."
            className="w-full px-3 py-4 bg-transparent focus:outline-none text-gray-900 dark:text-white placeholder-gray-400"
          />
          <kbd className="hidden sm:inline-flex items-center px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-slate-800 rounded">
            ESC
          </kbd>
        </div>

        {/* Results */}
        <div
          ref={listRef}
          id="command-palette-results"
          role="listbox"
          aria-label="Results"
          className="max-h-[60vh] overflow-y-auto"
        >
          {groupedResults.length === 0 ? (
            <div className="px-4 py-8 text-center text-gray-500">
              <MagnifyingGlassIcon className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              <p>No results for "{query}"</p>
              <p className="text-sm mt-1">Try searching for "stripe", "quote", or "customer"</p>
            </div>
          ) : (
            groupedResults.map(({ category, items }) => (
              <div key={category} role="group" aria-label={category}>
                {/* Category Header — aria-hidden because the group's aria-label
                    already carries this text; announcing it twice is noise. */}
                <div aria-hidden="true" className="px-4 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide bg-gray-50 dark:bg-slate-800/50">
                  {category}
                </div>
                {/* Items */}
                {items.map((action) => {
                  globalIndex++;
                  const Icon = action.icon;
                  const isSelected = globalIndex === selectedIndex;
                  const idx = globalIndex;
                  return (
                    <button
                      key={action.id}
                      id={`command-palette-option-${idx}`}
                      role="option"
                      aria-selected={isSelected}
                      data-index={idx}
                      onClick={() => run(action)}
                      className={`command-palette__item w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                        isSelected
                          ? 'is-selected'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-800'
                      }`}
                    >
                      <Icon className={`command-palette__item-icon w-5 h-5 flex-shrink-0 ${isSelected ? '' : 'text-gray-400'}`} />
                      <span className="flex-1">{action.label}</span>
                      {isSelected && (
                        <kbd className="hidden sm:inline-flex items-center px-2 py-0.5 text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-slate-700 rounded">
                          ↵
                        </kbd>
                      )}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 text-xs text-gray-500 border-t border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/50 flex items-center justify-between">
          <span>
            <kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-slate-700 rounded text-gray-600 dark:text-gray-400">↑</kbd>
            <kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-slate-700 rounded text-gray-600 dark:text-gray-400 ml-1">↓</kbd>
            <span className="ml-2">to navigate</span>
          </span>
          <span>
            <kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-slate-700 rounded text-gray-600 dark:text-gray-400">↵</kbd>
            <span className="ml-2">to select</span>
          </span>
          <span className="hidden sm:inline">
            <kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-slate-700 rounded text-gray-600 dark:text-gray-400">Esc</kbd>
            <span className="ml-2">to close</span>
          </span>
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;


